import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet,
    Chip
} from '@mui/joy';
import GradeIcon from '@mui/icons-material/Grade';
import axiosConfig from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const testMarksTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': {
        width: 200,
        minWidth: 200,
        whiteSpace: 'normal',
        wordBreak: 'break-word'
    },
    '& th:nth-of-type(2), & td:nth-of-type(2)': {
        width: 120,
        minWidth: 120,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(3), & td:nth-of-type(3)': {
        width: 100,
        minWidth: 100,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(4), & td:nth-of-type(4)': {
        width: 130,
        minWidth: 130,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(5), & td:nth-of-type(5)': {
        width: 100,
        minWidth: 100,
        whiteSpace: 'nowrap'
    }
};

const cellTextSx = {
    whiteSpace: 'inherit',
    wordBreak: 'inherit'
};

const TestMarks = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [testMarks, setTestMarks] = useState([]);

    useEffect(() => {
        fetchTestMarks();
    }, []);

    const fetchTestMarks = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/test-marks');
            setTestMarks(response.data.testMarks || []);
        } catch (err) {
            console.error('Error fetching test marks:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load test marks data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading test marks...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (testMarks.length === 0) {
        return (
            <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto', width: '100%', minWidth: 0 }}>
                <Card variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="h4" sx={{ fontWeight: 'bold', color: '#6e7072' }}>
                        Test marks data not yet available
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto', width: '100%', minWidth: 0 }}>
            <Card variant="outlined">
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <GradeIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            Test Marks
                        </Typography>
                    </Box>
                    <Sheet variant="outlined" sx={tableSheetSx}>
                        <Table
                            aria-label="test marks table"
                            sx={testMarksTableSx}
                        >
                            <thead>
                                <tr>
                                    <th>Test</th>
                                    <th>Date</th>
                                    <th>Full Marks</th>
                                    <th>Marks Obtained</th>
                                    <th>Percentage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {testMarks.map((mark, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {mark.testName || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {mark.testDate || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {mark.fullMarks || '-'}
                                            </Typography>
                                        </td>
                                        <td>
                                            {mark.isAbsent ? (
                                                <Chip
                                                    size="sm"
                                                    sx={{
                                                        backgroundColor: '#ff4444',
                                                        color: 'white',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    A
                                                </Chip>
                                            ) : (
                                                <Typography level="body-md" sx={cellTextSx}>
                                                    {mark.marksObtained !== null ? mark.marksObtained : '-'}
                                                </Typography>
                                            )}
                                        </td>
                                        <td>
                                            <Typography level="body-md" sx={cellTextSx}>
                                                {mark.percentage}%
                                            </Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TestMarks;
